/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    PWA_INSTALL_MENU_ITEM
 * Date:         2026-03-07
 * Modified By:  Claude
 *
 * Description:
 * Custom hook to capture the browser's beforeinstallprompt event and expose
 * a trigger for the native Android PWA install dialog.
 * Only activates on Android devices (Chrome). Hidden on desktop, iOS, Firefox.
 *
 * File Size Check:
 * - Current: NEW FILE
 * - Projected: ~75 lines
 * - Decision: Proceed (within 100 line hook limit)
 *
 * Related Files:
 * - client/src/components/layout/Navbar.tsx (consumer)
 * - client/public/service-worker.js (required for beforeinstallprompt)
 */

import { useState, useEffect, useCallback, useRef } from "react";

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

interface UsePwaInstallResult {
  /** true when the install prompt is available and the device is Android */
  canInstall: boolean;
  /** Call this to show the native install dialog */
  promptInstall: () => void;
}

/**
 * Hook for PWA install prompt — Android only.
 *
 * Listens for the browser's `beforeinstallprompt` event (Chrome on Android),
 * checks that the user agent is Android, and provides a trigger to show
 * the native install dialog.
 *
 * Returns `canInstall: false` on desktop, iOS, Firefox, or if the app is
 * already installed (standalone mode).
 */
export function usePwaInstall(): UsePwaInstallResult {
  const deferredPrompt = useRef<BeforeInstallPromptEvent | null>(null);
  const [canInstall, setCanInstall] = useState(false);

  useEffect(() => {
    // Only activate on Android
    const isAndroid = /Android/i.test(navigator.userAgent);
    if (!isAndroid) return;

    // Already installed (standalone mode) — hide the option
    const isStandalone =
      window.matchMedia("(display-mode: standalone)").matches ||
      (navigator as any).standalone === true;
    if (isStandalone) return;

    const handleBeforeInstall = (e: Event) => {
      e.preventDefault();
      deferredPrompt.current = e as BeforeInstallPromptEvent;
      setCanInstall(true);
    };

    const handleAppInstalled = () => {
      deferredPrompt.current = null;
      setCanInstall(false);
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstall);
    window.addEventListener("appinstalled", handleAppInstalled);

    return () => {
      window.removeEventListener("beforeinstallprompt", handleBeforeInstall);
      window.removeEventListener("appinstalled", handleAppInstalled);
    };
  }, []);

  const promptInstall = useCallback(() => {
    if (!deferredPrompt.current) return;

    deferredPrompt.current.prompt();
    deferredPrompt.current.userChoice.then((choice) => {
      if (choice.outcome === "accepted") {
        setCanInstall(false);
      }
      deferredPrompt.current = null;
    });
  }, []);

  return { canInstall, promptInstall };
}

export default usePwaInstall;
